import { MOTION } from '@zerohuman/ui'
import { type ComputedRef, computed, type Ref, ref } from 'vue'
import type { ChatDetail, ChatMessageRow, ChatToolCallDecision, GateDecision } from '@/api'
import { GATE_CARD_CLOSE_MS, useGateDecisions } from '@/composables/useGateDecisions'
import { awaitingConfirmation } from '@/lib/chat'

/** A chat decision in the tone /gates gives it: either way of allowing is an approval, a denial a rejection. */
export const CALL_DECIDED: Readonly<Record<ChatToolCallDecision, GateDecision>> = {
    allow: 'approve',
    always: 'approve',
    deny: 'reject',
}

/** What a decision on a tool call came to: whether the API recorded it, and why not when it did not. */
export type ChatToolCallOutcome = { recorded: boolean; refusal: unknown }

export type ChatToolCalls = {
    /** The tool call whose Allow / Always allow / Deny is on its way to the API: every card's buttons wait on it. */
    decidingCallId: Ref<string | null>
    /** The calls drawn as cards, oldest first: each waiting on you, or just decided and not yet gone. */
    waitingCalls: ComputedRef<ChatMessageRow[]>
    /** Whether a row is drawn as a call's card rather than in the log. */
    onCard: (row: ChatMessageRow) => boolean
    /** The decision you just made on a call, while its card is still showing it. */
    callDecided: (row: ChatMessageRow) => GateDecision | undefined
    /** Whether a decision can be taken on `row` now: it is a call, none is on its way, and it is not decided yet. */
    canDecide: (row: ChatMessageRow) => boolean
    /**
     * Decides `row`, the card marking it at once. `record` asks the API (`api.decideChatToolCall`, made by the page,
     * which asks the AI account guard first) and answers with the conversation as it then stands: the call ran or was
     * refused, and the member's turn carried on. `landed` takes that conversation. Resolves once both the API has
     * answered and the card's hold has run; null when nothing was decided (see `canDecide`).
     */
    decide: (
        row: ChatMessageRow,
        decision: ChatToolCallDecision,
        record: (callId: string) => Promise<ChatDetail>,
        landed: (chat: ChatDetail) => unknown,
    ) => Promise<ChatToolCallOutcome | null>
}

/**
 * The tool calls a member asks you about before running them, as both conversations draw them (#244): the Chat page
 * and Create task share these cards' state, so the two cannot drift. A decision shows on the call's card at once,
 * which fades and closes up its space without waiting on the API, as a decided gate does; a card showing its
 * decision takes no other. If the API refuses, the card comes back as it was. The card leaves the page once both the
 * hold has run and the API has answered, so a call the API is still running after the hold stays a closed card (see
 * `onCard`), and a refusal that lands then still brings it back.
 */
export function useChatToolCalls(messages: () => readonly ChatMessageRow[]): ChatToolCalls {
    const decidingCallId = ref<string | null>(null)
    /**
     * The tool calls you have just decided, as /gates and the run page show a decided gate (keyed by the call's id):
     * the card marks the decision at once and leaves MOTION.decisionMs plus GATE_CARD_CLOSE_MS later, once it has
     * faded and its space has closed, without waiting on the API, so a read that already says decided cannot cut it
     * short.
     */
    const callDecisions = useGateDecisions(MOTION.decisionMs + GATE_CARD_CLOSE_MS)
    /**
     * Each just-decided call as its card drew it, from the click until both the API has answered and the hold has run
     * (`decide` resolves on both): once decided, the row no longer carries what it would run with.
     */
    const heldCalls = ref<ReadonlyMap<string, ChatMessageRow>>(new Map())

    /**
     * Whether a row is drawn as a call's card rather than in the log: waiting on you, or just decided and not yet gone.
     * A decided call is still a card while it is held, until the API has answered: deciding runs the tool and carries
     * the member's turn on, which can outlast the hold, and by then the card has faded and its space has closed, so it
     * sits closed rather than the call being drawn in the log as still waiting. A refusal then opens it again.
     */
    function onCard(row: ChatMessageRow): boolean {
        const waiting = awaitingConfirmation(row)
        if (row.role !== 'tool' || !row.toolCallId) {
            return waiting
        }
        return heldCalls.value.has(row.toolCallId) || callDecisions.stays(row.toolCallId, waiting)
    }

    const waitingCalls = computed((): ChatMessageRow[] =>
        messages()
            .filter(onCard)
            .map(row => heldCalls.value.get(row.toolCallId ?? '') ?? row),
    )

    function callDecided(row: ChatMessageRow): GateDecision | undefined {
        return row.toolCallId ? callDecisions.decidedOf(row.toolCallId) : undefined
    }

    function canDecide(row: ChatMessageRow): boolean {
        return !!row.toolCallId && decidingCallId.value === null && callDecided(row) === undefined
    }

    async function decide(
        row: ChatMessageRow,
        decision: ChatToolCallDecision,
        record: (callId: string) => Promise<ChatDetail>,
        landed: (chat: ChatDetail) => unknown,
    ): Promise<ChatToolCallOutcome | null> {
        const callId = row.toolCallId
        if (!callId || !canDecide(row)) {
            return null
        }
        decidingCallId.value = callId
        heldCalls.value = new Map([...heldCalls.value, [callId, row]])
        /** Why the API refused, kept here: `decide` only says whether the decision was recorded (#1256). */
        let refusal: unknown
        const recorded = await callDecisions.decide(callId, CALL_DECIDED[decision], async () => {
            let decided: ChatDetail
            try {
                // The call runs (or is refused) and the member's turn carries on before this returns, so the reply
                // arrives with it.
                decided = await record(callId)
            } catch (caught) {
                refusal = caught
                throw caught
            } finally {
                decidingCallId.value = null
            }
            await landed(decided)
        })
        heldCalls.value = new Map([...heldCalls.value].filter(([heldId]) => heldId !== callId))
        return { recorded, refusal }
    }

    return { decidingCallId, waitingCalls, onCard, callDecided, canDecide, decide }
}
